<template>
    <NuxtLink
        :to="{ name: 'vzdelavanie-slug', params: { slug: skill.slug || skill.id } }"
        class="SkillRow group flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-surface-card"
    >
        <!-- Skill Image/Icon -->
        <div class="relative size-14 shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-subtle">
            <img
                v-if="skill.coverImg"
                :src="skill.coverImg"
                :alt="skill.title"
                class="size-full object-cover"
                loading="lazy"
            />
            <div v-else class="flex size-full items-center justify-center bg-primary-50 dark:bg-primary-900/30">
                <Icon :name="typeIcon" class="size-6 text-primary-400" />
            </div>
        </div>

        <!-- Main Content -->
        <div class="min-w-0 flex-1">
            <!-- Top row: Title and Type badge -->
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h3 class="truncate text-base font-semibold text-gray-900 dark:text-white">
                    {{ skill.title }}
                </h3>
                <span
                    v-if="skillTypeName"
                    class="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                >
                    {{ skillTypeName }}
                </span>
            </div>

            <!-- Meta row: Date, location, online -->
            <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 dark:text-txt-body">
                <!-- Date for events -->
                <span v-if="isEvent && startDate" class="flex items-center gap-1">
                    <Icon name="heroicons:calendar" class="size-3.5 text-gray-400 dark:text-txt-body" />
                    {{ startDate }}
                </span>

                <!-- Location or Online -->
                <span v-if="isEvent && skill.isOnline" class="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                    <Icon name="heroicons:globe-alt" class="size-3.5" />
                    Online
                </span>
                <span v-else-if="isEvent && skill.city" class="flex items-center gap-1 text-gray-500 dark:text-txt-body">
                    <Icon name="heroicons:map-pin" class="size-3.5" />
                    {{ skill.city }}
                </span>
            </div>

            <!-- Topics row -->
            <div v-if="topics && topics.length" class="mt-2 hidden flex-wrap gap-1.5 sm:flex">
                <span
                    v-for="topic in topics.slice(0, 3)"
                    :key="topic"
                    class="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-subtle dark:text-txt-body"
                >
                    {{ topic }}
                </span>
                <span v-if="topics.length > 3" class="px-1 text-xs text-gray-400 dark:text-txt-body">
                    +{{ topics.length - 3 }}
                </span>
            </div>
        </div>

        <!-- Action Arrow -->
        <div class="hidden shrink-0 items-center gap-2 sm:flex">
            <span class="text-sm font-medium text-primary-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-primary-400">
                Detail
            </span>
            <Icon name="heroicons:chevron-right" class="size-5 text-gray-400 dark:text-txt-body transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500" />
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Skill } from '#shared/types/Skill'
import { SkillType } from '#shared/types/Skill'
import { skillTypeDict } from '#shared/types/dictionary'
import { useSkills } from '@/composables/skills/useSkills'

interface Props {
    skill: Skill
}

const props = defineProps<Props>()

const { formatSkillDate } = useSkills()

const startDate = computed(() => {
    if (!props.skill.startDate) return null
    return formatSkillDate(props.skill)
})

const topics = computed(() => props.skill?.topics || [])

// Skill type helpers
const isEvent = computed(() =>
    props.skill.type === SkillType.event ||
    props.skill.type === SkillType.studyOpportunity
)

const skillTypeName = computed(() => {
    if (!props.skill.type) return ''
    return skillTypeDict[props.skill.type as SkillType] || ''
})

const typeIcon = computed(() => {
    const iconMap: Record<string, string> = {
        [SkillType.kurz]: 'heroicons:academic-cap',
        [SkillType.event]: 'heroicons:calendar',
        [SkillType.workgroup]: 'heroicons:user-group',
        [SkillType.studyOpportunity]: 'heroicons:book-open'
    }
    return iconMap[props.skill.type] || 'heroicons:book-open'
})
</script>
